import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Trash2, Edit, MapPin, Phone, Mail, Camera, Eye, Send, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Clients = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientOpen, setClientOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [search, setSearch] = useState('');
  
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  
  const [addressForm, setAddressForm] = useState({
    address: '',
    label: '',
    email: '',
    phone: '',
    is_primary: false
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients/all');
      setClients(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', clientForm.name);
      if (clientForm.email) formData.append('email', clientForm.email);
      if (clientForm.phone) formData.append('phone', clientForm.phone);
      if (clientForm.address) formData.append('address', clientForm.address);
      if (clientForm.notes) formData.append('notes', clientForm.notes);

      if (editingClient) {
        await axiosInstance.put(`/clients/${editingClient.id}`, formData);
        toast.success('Cliente atualizado!');
      } else {
        await axiosInstance.post('/clients', formData);
        toast.success('Cliente criado!');
      }
      setClientOpen(false);
      setEditingClient(null);
      setClientForm({ name: '', email: '', phone: '', address: '', notes: '' });
      fetchClients();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    
    // Se está editando, chamar função de atualização
    if (editingAddress) {
      return handleUpdateAddress(e);
    }
    
    try {
      const formData = new FormData();
      formData.append('address', addressForm.address);
      if (addressForm.label) formData.append('label', addressForm.label);
      if (addressForm.email) formData.append('email', addressForm.email);
      if (addressForm.phone) formData.append('phone', addressForm.phone);
      formData.append('is_primary', addressForm.is_primary);
      
      await axiosInstance.post(`/admin/clients/${selectedClient.id}/addresses`, formData);
      toast.success('Endereço adicionado!');
      setAddressOpen(false);
      setAddressForm({ address: '', label: '', email: '', phone: '', is_primary: false });
      fetchClients();
    } catch (error) {
      toast.error('Erro ao adicionar endereço');
    }
  };

  const handleEditAddress = (client, address) => {
    setSelectedClient(client);
    setEditingAddress(address);
    setAddressForm({
      address: address.address,
      label: address.label || '',
      email: address.email || '',
      phone: address.phone || '',
      is_primary: address.is_primary || false
    });
    setAddressOpen(true);
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    if (!selectedClient || !editingAddress) return;
    
    try {
      const formData = new FormData();
      formData.append('address', addressForm.address);
      if (addressForm.label) formData.append('label', addressForm.label);
      if (addressForm.email) formData.append('email', addressForm.email);
      if (addressForm.phone) formData.append('phone', addressForm.phone);
      formData.append('is_primary', addressForm.is_primary);
      
      await axiosInstance.put(`/admin/clients/${selectedClient.id}/addresses/${editingAddress.id}`, formData);
      toast.success('Endereço atualizado!');
      setAddressOpen(false);
      setEditingAddress(null);
      setAddressForm({ address: '', label: '', email: '', phone: '', is_primary: false });
      fetchClients();
    } catch (error) {
      toast.error('Erro ao atualizar endereço');
    }
  };

  const handleDeleteAddress = async (clientId, addressId) => {
    if (!window.confirm('Tem certeza que deseja deletar este endereço?')) {
      return;
    }
    
    try {
      console.log('Deletando endereço:', { clientId, addressId });
      const response = await axiosInstance.delete(`/admin/clients/${clientId}/addresses/${addressId}`);
      console.log('Resposta:', response.data);
      toast.success('Endereço deletado!');
      fetchClients();
    } catch (error) {
      console.error('Erro ao deletar endereço:', error);
      toast.error('Erro ao deletar endereço: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Deseja deletar este cliente?')) return;
    try {
      await axiosInstance.delete(`/clients/${id}`);
      toast.success('Cliente deletado!');
      fetchClients();
    } catch (error) {
      toast.error('Erro ao deletar cliente');
    }
  };

  const handleInvite = async (client) => {
    const targetEmail = client.email || (client.addresses?.[0]?.email);
    if (!targetEmail) {
      toast.error('Deze klant heeft geen e-mailadres');
      return;
    }
    if (!window.confirm(`Uitnodiging sturen naar ${targetEmail}?`)) return;
    try {
      const r = await axiosInstance.post(`/clients/${client.id}/invite`);
      toast.success(`Uitnodiging verzonden naar ${r.data.email}`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodiging versturen mislukt');
    }
  };

  const handleInviteAddress = async (client, addr) => {
    if (!addr.email) {
      toast.error(`${addr.label || 'Deze klant'} heeft geen e-mailadres — voeg eerst een e-mail toe.`);
      return;
    }
    const who = addr.label ? `${addr.label} (${addr.email})` : addr.email;
    if (!window.confirm(`Uitnodiging sturen naar ${who}?`)) return;
    try {
      const r = await axiosInstance.post(`/clients/${client.id}/addresses/${addr.id}/invite`);
      toast.success(`Uitnodiging verzonden naar ${r.data.name || r.data.email}`);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodiging versturen mislukt');
    }
  };

  if (!user.is_admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-800 text-lg">Acesso restrito a administradores</p>
      </div>
    );
  }

  // Filter logic — show empresa if its name OR any address (label/address/email/phone) matches the search
  const searchLower = search.trim().toLowerCase();
  const matchesAddr = (a) => {
    if (!searchLower) return false;
    return (
      ((a.label || '').toLowerCase().includes(searchLower)) ||
      ((a.address || '').toLowerCase().includes(searchLower)) ||
      ((a.email || '').toLowerCase().includes(searchLower)) ||
      ((a.phone || '').toLowerCase().includes(searchLower))
    );
  };
  const filteredClients = useMemo(() => {
    if (!searchLower) return clients;
    return clients.filter((c) => {
      if ((c.name || '').toLowerCase().includes(searchLower)) return true;
      if ((c.email || '').toLowerCase().includes(searchLower)) return true;
      if ((c.address || '').toLowerCase().includes(searchLower)) return true;
      if ((c.phone || '').toLowerCase().includes(searchLower)) return true;
      return (c.addresses || []).some(matchesAddr);
    });
  }, [clients, searchLower]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800">
            Bedrijven & Klanten
            <span className="text-base text-gray-400 font-normal ml-3" data-testid="bedrijven-counter">
              ({clients.length} {clients.length === 1 ? 'bedrijf' : 'bedrijven'} · {clients.reduce((s, c) => s + ((c.addresses || []).length), 0)} klanten)
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Cada bedrijf agrupa os seus klanten — 1 endereço = 1 klant.</p>
        </div>
        <Button
          onClick={() => {
            setEditingClient(null);
            setClientForm({ name: '', email: '', phone: '', address: '', notes: '' });
            setClientOpen(true);
          }}
          className="bg-[#3da5a5] hover:bg-[#2d8585]"
          data-testid="new-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      {/* Search/filter */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek bedrijf, klant, adres of e-mail..."
          className="pl-9 pr-9 bg-white border-gray-200"
          data-testid="clients-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
            data-testid="clients-search-clear"
            aria-label="Wissen"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {filteredClients.length === 0 && search ? (
        <p className="text-sm text-gray-500 italic" data-testid="clients-no-results">
          Geen resultaten voor "{search}". Probeer een ander zoekterm of clear het filter.
        </p>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map(client => (
          <Card key={client.id} className="bg-white shadow-sm border-gray-200" data-testid={`client-card-${client.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="cursor-pointer hover:underline" onClick={() => navigate(`/clients/${client.id}`)} data-testid={`open-client-${client.id}`}>
                  <CardTitle className="text-gray-800 text-lg">{client.name}</CardTitle>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="text-[#3da5a5] border-[#3da5a5]/30"
                    title="360° weergave openen"
                    data-testid={`view-client-${client.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingClient(client);
                      setClientForm(client);
                      setClientOpen(true);
                    }}
                    className="text-blue-400 border-blue-400/30"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleInvite(client)}
                    className="text-amber-500 border-amber-500/30"
                    title="Uitnodiging sturen om klantenportaal te activeren"
                    data-testid={`invite-client-${client.id}`}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteClient(client.id)}
                    className="text-red-400 border-red-400/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {client.email && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Mail className="w-4 h-4" />
                    {client.email}
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Phone className="w-4 h-4" />
                    {client.phone}
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <MapPin className="w-4 h-4" />
                    {client.address}
                  </div>
                )}
                
                {/* Klanten reais = endereços */}
                {client.addresses && client.addresses.length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-gray-700 font-semibold mb-2">Klanten ({client.addresses.length}):</p>
                    {client.addresses.map(addr => {
                      const isMatch = searchLower && matchesAddr(addr);
                      return (
                      <div
                        key={addr.id}
                        className={`flex justify-between items-start mb-2 p-2 rounded transition-all ${isMatch ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'bg-gray-50'}`}
                        data-testid={`klant-row-${addr.id}${isMatch ? '-match' : ''}`}
                      >
                        <div className="flex-1 min-w-0">
                          {addr.label && <p className="text-[#3da5a5] text-xs font-semibold">{addr.label}</p>}
                          <p className="text-gray-700 text-xs">{addr.address}</p>
                          {addr.email && (
                            <p className="text-gray-500 text-xs flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{addr.email}</p>
                          )}
                          {addr.phone && (
                            <p className="text-gray-500 text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{addr.phone}</p>
                          )}
                          {addr.is_primary && <span className="text-yellow-500 text-xs">⭐ Principal</span>}
                          {addr.invited_at && (
                            <span className="ml-1 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" title={`Uitgenodigd op ${new Date(addr.invited_at).toLocaleDateString('nl-NL')}`}>
                              ✉ Uitgenodigd
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleInviteAddress(client, addr);
                            }}
                            className={`h-6 w-6 p-0 flex items-center justify-center ${addr.email ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 cursor-not-allowed'}`}
                            title={addr.email ? `Uitnodiging sturen naar ${addr.email}` : 'Voeg eerst een e-mailadres toe'}
                            disabled={!addr.email}
                            data-testid={`invite-address-${addr.id}`}
                          >
                            <Send className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const addrStr = addr.address || '';
                              navigate(`/photos?client=${encodeURIComponent(client.name)}&address=${encodeURIComponent(addrStr)}&upload=1`);
                            }}
                            className="text-[#3da5a5] hover:text-[#2d8585] h-6 w-6 p-0 flex items-center justify-center"
                            title="Foto's toevoegen aan dit adres"
                            data-testid={`add-photos-${addr.id}`}
                          >
                            <Camera className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditAddress(client, addr);
                            }}
                            className="text-blue-500 hover:text-blue-600 h-6 w-6 p-0 flex items-center justify-center"
                            data-testid={`edit-address-${addr.id}`}
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteAddress(client.id, addr.id);
                            }}
                            className="text-red-500 hover:text-red-600 h-6 w-6 p-0 flex items-center justify-center"
                            data-testid={`delete-address-${addr.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
                
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedClient(client);
                    setEditingAddress(null);
                    setAddressForm({ address: '', label: '', email: '', phone: '', is_primary: false });
                    setAddressOpen(true);
                  }}
                  className="w-full mt-2 bg-blue-600 hover:bg-blue-700"
                  data-testid={`add-address-${client.id}`}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar Klant
                </Button>

                {client.notes && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-gray-400 text-xs">{client.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog: Cliente */}
      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editingClient ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClientSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-800">Nome *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                required
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>
            <div>
              <Label className="text-gray-800">Email</Label>
              <Input
                type="email"
                value={clientForm.email}
                onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>
            <div>
              <Label className="text-gray-800">Telefone</Label>
              <Input
                value={clientForm.phone}
                onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>
            <div>
              <Label className="text-gray-800">Endereço Principal</Label>
              <Input
                value={clientForm.address}
                onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
              />
            </div>
            <div>
              <Label className="text-gray-800">Notas</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                className="bg-gray-50 border-white/30 text-gray-800"
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full bg-[#3da5a5] hover:bg-[#2d8585]">
              {editingClient ? 'Atualizar' : 'Criar'} Empresa
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Adicionar/Editar Endereço (Cliente) */}
      <Dialog open={addressOpen} onOpenChange={(open) => {
        setAddressOpen(open);
        if (!open) {
          setEditingAddress(null);
          setAddressForm({ address: '', label: '', email: '', phone: '', is_primary: false });
        }
      }}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingAddress ? 'Editar Klant' : 'Adicionar Klant'}
            </DialogTitle>
            <p className="text-gray-500 text-sm">Empresa: {selectedClient?.name}</p>
          </DialogHeader>
          <form onSubmit={handleAddAddress} className="space-y-4">
            <div>
              <Label className="text-gray-800">Nome do Klant (Rótulo)</Label>
              <Input
                value={addressForm.label}
                onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                placeholder="Ex: Fiona, OLVG WEST, etc"
                className="bg-gray-50 border-gray-200 text-gray-800"
                data-testid="address-label-input"
              />
            </div>
            <div>
              <Label className="text-gray-800">Endereço *</Label>
              <Input
                value={addressForm.address}
                onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                required
                placeholder="Rua, número, cidade"
                className="bg-gray-50 border-gray-200 text-gray-800"
                data-testid="address-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-800">Email</Label>
                <Input
                  type="email"
                  value={addressForm.email}
                  onChange={(e) => setAddressForm({ ...addressForm, email: e.target.value })}
                  placeholder="cliente@email.com"
                  className="bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="address-email-input"
                />
              </div>
              <div>
                <Label className="text-gray-800">Telefone</Label>
                <Input
                  value={addressForm.phone}
                  onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                  placeholder="+31 ..."
                  className="bg-gray-50 border-gray-200 text-gray-800"
                  data-testid="address-phone-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={addressForm.is_primary}
                onChange={(e) => setAddressForm({ ...addressForm, is_primary: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_primary" className="text-gray-800 cursor-pointer">
                Marcar como endereço principal
              </Label>
            </div>
            <Button type="submit" className="w-full bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="save-address-btn">
              {editingAddress ? 'Atualizar Klant' : 'Adicionar Klant'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
